import {
	type Action,
	type BoundActions,
	createActionsHook,
	createHook,
	createStore,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

export type RovoApp = {
	source: string;
	friendlyName: string;
	iconUrl: string;
};
export type FiltererdOrgKnowledge = {
	// enabled with filtered apps, in absence of filtered apps, it's enabled for all apps
	filteredApps: RovoApp[];
};
export type OrgKnowledge = 'disabled' | FiltererdOrgKnowledge;

export type ReasoningMode =
	| 'auto'
	| 'fast'
	| 'deep-research'
	| 'think-deeper'
	| 'think-deeper-marathon'
	| 'think-deeper-orchestrator';

type ChatModeInternal = {
	reasoningMode: ReasoningMode;
	webSearchEnabled: boolean;
	orgKnowledge: OrgKnowledge;
	autoApplyEnabled: boolean;
	useCurrentPageContext: boolean;
};

export interface ChatMode extends ChatModeInternal {
	/**
	 * Derived from orgKnowledge
	 * appFilters === undefined means all org knowledge applied and no apps filtered.
	 * appFilters === [] means no org knowledge applied at all.
	 * appFilters === [app1, app2, ...] means org knowledge applied and apps filtered.
	 */
	appFilters: RovoApp[] | undefined;
	/**
	 * Derived from reasoningMode === 'deep-research'
	 */
	deepResearchEnabled: boolean;
	/**
	 * Derived from reasoningMode === 'think-deeper'
	 */
	thinkDeeperEnabled: boolean;
	fastModeEnabled: boolean;
	/**
	 * Derived from reasoningMode === 'auto'
	 */
	autoReasoningEnabled: boolean;
}

export type ChatModeState = {
	[key: string]: ChatModeInternal;
};

const getWebSearchDefaultFromStorage = (): boolean | null => {
	if (typeof globalThis === 'undefined' || !globalThis.document || !globalThis.localStorage) {
		return null;
	}
	const value = globalThis.localStorage.getItem('rovo-chat-web-search-default-on');
	if (value === null) {
		return null;
	}
	return value !== 'false';
};

export const getDerivedDefaultChatMode = (): ChatModeInternal => {
	const storageValue = getWebSearchDefaultFromStorage();

	// If localStorage explicitly says it's off, default should be off
	// Otherwise, use the experiment flag value
	const webSearchEnabled =
		storageValue === false
			? false
			: FeatureGates.getExperimentValue('rovo_web_search_default_on', 'isEnabled', false);

	return {
		...{
			reasoningMode: 'auto',
			webSearchEnabled,
			orgKnowledge: {
				filteredApps: [],
			},
			autoApplyEnabled: true,
		},
		useCurrentPageContext: fg('disable_default_on_current_page_as_context') ? false : true,
	};
};

const updateMode: (
	key: string,
	mode: Partial<ChatModeInternal>,
) => Action<ChatModeState, any, any> = (key: string, mode: Partial<ChatModeInternal>) => {
	return ({ setState, getState }) => {
		const state = getState()[key] || getDerivedDefaultChatMode();
		setState({
			[key]: {
				...state,
				...mode,
			},
		});
	};
};

const store = createStore({
	initialState: {} as ChatModeState,
	actions: {
		setReasoningMode:
			(
				key: string,
				reasoningMode: ReasoningMode,
			): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { reasoningMode: reasoningMode }));
			},
		resetReasoningMode:
			(key: string): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { reasoningMode: getDerivedDefaultChatMode().reasoningMode }));
			},
		setWebSearchEnabled:
			(key: string, isEnabled: boolean): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { webSearchEnabled: isEnabled }));
			},
		setOrgKnowledge:
			(
				key: string,
				orgKnowledge: OrgKnowledge,
			): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { orgKnowledge: orgKnowledge }));
			},
		setUseCurrentPageContext:
			(key: string, isEnabled: boolean): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
			({ dispatch }) => {
				dispatch(updateMode(key, { useCurrentPageContext: isEnabled }));
			},
		setChatMode:
			(
				key: string,
				chatMode: Partial<
					Omit<
						ChatMode,
						'orgKnowledge' | 'deepResearchEnabled' | 'thinkDeeperEnabled' | 'fastModeEnabled'
					>
				>,
			): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
			({ dispatch }) => {
				let updatedChatMode: Partial<ChatMode> = chatMode;
				let orgKnowledge: OrgKnowledge = getDerivedDefaultChatMode().orgKnowledge;
				if (chatMode.appFilters === undefined) {
					orgKnowledge = getDerivedDefaultChatMode().orgKnowledge;
				} else if (chatMode.appFilters.length > 0) {
					orgKnowledge = {
						filteredApps: chatMode.appFilters,
					};
				} else {
					orgKnowledge = 'disabled';
				}
				updatedChatMode = {
					...chatMode,
					orgKnowledge: orgKnowledge,
				};
				dispatch(updateMode(key, updatedChatMode));
			},
		getChatMode:
			(
				key: string,
			): (({ getState }: StoreActionApi<ChatModeState>) => {
				appFilters: RovoApp[] | undefined;
				deepResearchEnabled: boolean;
				thinkDeeperEnabled: boolean;
				fastModeEnabled: boolean;
				autoReasoningEnabled: boolean;
				reasoningMode: ReasoningMode;
				webSearchEnabled: boolean;
				orgKnowledge: OrgKnowledge;
				autoApplyEnabled: boolean;
				useCurrentPageContext: boolean;
			}) =>
			({ getState }) => {
				const state = getState()[key] || getDerivedDefaultChatMode();
				return {
					...state,
					...getDerivedReasoningMode(state.reasoningMode),
					appFilters: getAppFilters(state.orgKnowledge),
				};
			},
		clearMode:
			(key: string): (({ dispatch }: StoreActionApi<ChatModeState>) => void) =>
			({ dispatch }) => {
				dispatch(updateMode(key, getDerivedDefaultChatMode()));
			},
	},
});

const getDerivedReasoningMode = (reasoningMode: ReasoningMode) => {
	const isThinkDeeperMode =
		reasoningMode === 'think-deeper' ||
		reasoningMode === 'think-deeper-marathon' ||
		reasoningMode === 'think-deeper-orchestrator';

	return {
		deepResearchEnabled: reasoningMode === 'deep-research',
		thinkDeeperEnabled: isThinkDeeperMode,
		fastModeEnabled: reasoningMode === 'fast',
		autoReasoningEnabled: reasoningMode === 'auto',
	};
};

const getAppFilters = (orgKnowledge: OrgKnowledge) => {
	if (orgKnowledge === 'disabled') {
		// no org knowledge applied at all.
		return [];
	} else if (orgKnowledge.filteredApps.length === 0) {
		// all org knowledge applied and no apps filtered.
		return undefined;
	} else {
		// org knowledge applied and apps filtered.
		return orgKnowledge.filteredApps;
	}
};

const selector = (state: ChatModeState, key: string) => {
	const chatMode = state[key] || getDerivedDefaultChatMode();
	return {
		...chatMode,
		...getDerivedReasoningMode(chatMode.reasoningMode),
		appFilters: getAppFilters(chatMode.orgKnowledge),
	};
};
export const useChatModeStore: HookFunction<
	{
		appFilters: RovoApp[] | undefined;
		deepResearchEnabled: boolean;
		thinkDeeperEnabled: boolean;
		fastModeEnabled: boolean;
		autoReasoningEnabled: boolean;
		reasoningMode: ReasoningMode;
		webSearchEnabled: boolean;
		orgKnowledge: OrgKnowledge;
		autoApplyEnabled: boolean;
		useCurrentPageContext: boolean;
	},
	BoundActions<
		ChatModeState,
		{
			setReasoningMode: (
				key: string,
				reasoningMode: ReasoningMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			resetReasoningMode: (key: string) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setWebSearchEnabled: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setOrgKnowledge: (
				key: string,
				orgKnowledge: OrgKnowledge,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setUseCurrentPageContext: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setChatMode: (
				key: string,
				chatMode: Partial<
					Omit<
						ChatMode,
						'orgKnowledge' | 'deepResearchEnabled' | 'thinkDeeperEnabled' | 'fastModeEnabled'
					>
				>,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			getChatMode: (key: string) => ({ getState }: StoreActionApi<ChatModeState>) => {
				appFilters: RovoApp[] | undefined;
				deepResearchEnabled: boolean;
				thinkDeeperEnabled: boolean;
				fastModeEnabled: boolean;
				autoReasoningEnabled: boolean;
				reasoningMode: ReasoningMode;
				webSearchEnabled: boolean;
				orgKnowledge: OrgKnowledge;
				autoApplyEnabled: boolean;
				useCurrentPageContext: boolean;
			};
			clearMode: (key: string) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
		}
	>,
	string
> = createHook(store, {
	selector,
});

export const useChatModeStoreActions: HookActionsFunction<
	BoundActions<
		ChatModeState,
		{
			setReasoningMode: (
				key: string,
				reasoningMode: ReasoningMode,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			resetReasoningMode: (key: string) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setWebSearchEnabled: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setOrgKnowledge: (
				key: string,
				orgKnowledge: OrgKnowledge,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setUseCurrentPageContext: (
				key: string,
				isEnabled: boolean,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			setChatMode: (
				key: string,
				chatMode: Partial<
					Omit<ChatMode, 'orgKnowledge' | 'deepResearchEnabled' | 'thinkDeeperEnabled'>
				>,
			) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
			getChatMode: (key: string) => ({ getState }: StoreActionApi<ChatModeState>) => {
				appFilters: RovoApp[] | undefined;
				deepResearchEnabled: boolean;
				thinkDeeperEnabled: boolean;
				fastModeEnabled: boolean;
				autoReasoningEnabled: boolean;
				reasoningMode: ReasoningMode;
				webSearchEnabled: boolean;
				orgKnowledge: OrgKnowledge;
				autoApplyEnabled: boolean;
				useCurrentPageContext: boolean;
			};
			clearMode: (key: string) => ({ dispatch }: StoreActionApi<ChatModeState>) => void;
		}
	>
> = createActionsHook(store);
export const useChatModeState: (key: string) => ChatMode = (key) => {
	const state = useChatModeStore(key)[0];
	return state;
};
